import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import type { IPartnerDocumentSection } from '../../../../lib/documentsAndLinks'
import { selectCockpitGeoMappingItems, selectCockpitPartnerData } from '../../../../model/selectors'
import {
  buildDocumentsNavigationTree,
  findDocumentsNavigatorNode,
} from '../../lib/buildDocumentsNavigationTree'
import { countDocuments } from '../../lib/countDocuments'
import type { TDocumentsGrouping } from '../../types'
import { DocumentsNavigator } from '../documentsNavigator/DocumentsNavigator'
import { DocumentsTreeContent } from '../documentsTreeContent/DocumentsTreeContent'
import { DocumentsTreeHeader } from '../documentsTreeHeader/DocumentsTreeHeader'

import styles from './DocumentsTreeView.module.scss'

export const DocumentsTreeView = ({
  sections,
  readOnly,
}: {
  sections: IPartnerDocumentSection[]
  readOnly: boolean
}) => {
  const [grouping, setGrouping] = useState<TDocumentsGrouping>('geo')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const partners = useSelector(selectCockpitPartnerData)
  const geoMapping = useSelector(selectCockpitGeoMappingItems)
  const nodes = useMemo(
    () => buildDocumentsNavigationTree({ sections, partners, geoMapping, grouping }),
    [sections, partners, geoMapping, grouping]
  )
  const changeGrouping = useCallback((next: TDocumentsGrouping) => {
    setGrouping(next)
    setSelectedKey(null)
  }, [])
  const selected = selectedKey ? findDocumentsNavigatorNode(nodes, selectedKey) : undefined
  const scopeSections = selected?.sections ?? sections
  return (
    <div className={styles.root}>
      <DocumentsNavigator
        nodes={nodes}
        grouping={grouping}
        onGroupingChange={changeGrouping}
        selectedKey={selected?.key ?? null}
        onSelect={setSelectedKey}
        totalCount={countDocuments(sections)}
      />
      <div className={styles.main}>
        <DocumentsTreeHeader label={selected?.label} sections={scopeSections} />
        <DocumentsTreeContent sections={scopeSections} readOnly={readOnly} />
      </div>
    </div>
  )
}
